import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { post } from '@/shared/api/client';
import { errorMessage } from '@/shared/api/client';
import { sessionKey } from '@/shared/api/session';
import { Alert, Button, Card, Field, Input } from '@/shared/ui';

const newPassword = z.string().min(12, 'Mínimo 12 caracteres');

export function ChangePasswordPage({ forced = true }: { forced?: boolean }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const schema = z.object({ current_password: z.string().min(1, 'Requerido'), new_password: newPassword, confirm: z.string() }).refine((v) => v.new_password === v.confirm, { path: ['confirm'], message: 'Las contraseñas no coinciden' });
  type Form = z.infer<typeof schema>;
  const { register, handleSubmit, formState: { errors } } = useForm<Form>({ resolver: zodResolver(schema) });
  const change = useMutation({
    mutationFn: (v: Form) => post<void>('/auth/password/change', { current_password: v.current_password, new_password: v.new_password }),
    onSuccess: async () => { await qc.invalidateQueries({ queryKey: sessionKey }); navigate(forced ? '/' : '/cuenta/seguridad', { replace: true }); },
  });
  return (
    <Card title={forced ? 'Crea tu contraseña definitiva' : 'Cambiar contraseña'}>
      {forced && <Alert kind="info">Estás usando una contraseña temporal. Debes definir una nueva antes de continuar.</Alert>}
      {change.isError && <Alert kind="error">{errorMessage(change.error)}</Alert>}
      <form onSubmit={handleSubmit((v) => change.mutate(v))} noValidate>
        <Field label={forced ? 'Contraseña temporal' : 'Contraseña actual'} htmlFor="current" error={errors.current_password?.message}>
          <Input id="current" type="password" autoComplete="current-password" invalid={!!errors.current_password} {...register('current_password')} />
        </Field>
        <Field label="Nueva contraseña" htmlFor="new" hint="Mínimo 12 caracteres. Evita contraseñas comunes o tu nombre de usuario." error={errors.new_password?.message}>
          <Input id="new" type="password" autoComplete="new-password" invalid={!!errors.new_password} {...register('new_password')} />
        </Field>
        <Field label="Confirmar nueva contraseña" htmlFor="confirm" error={errors.confirm?.message}>
          <Input id="confirm" type="password" autoComplete="new-password" invalid={!!errors.confirm} {...register('confirm')} />
        </Field>
        <Button type="submit" block loading={change.isPending}>Guardar contraseña</Button>
      </form>
    </Card>
  );
}

export function ResetRequestPage() {
  const schema = z.object({ email: z.email('Correo inválido') });
  const { register, handleSubmit, formState: { errors } } = useForm<{ email: string }>({ resolver: zodResolver(schema) });
  const req = useMutation({ mutationFn: (v: { email: string }) => post<{ detail: string }>('/auth/password/reset-request', v) });
  return (
    <Card title="Restablecer contraseña">
      {req.isSuccess ? (
        <Alert kind="success">Si el correo existe, recibirás un enlace para restablecer tu contraseña (válido por 30 minutos).</Alert>
      ) : (
        <form onSubmit={handleSubmit((v) => req.mutate(v))} noValidate>
          {req.isError && <Alert kind="error">{errorMessage(req.error)}</Alert>}
          <Field label="Correo corporativo" htmlFor="email" error={errors.email?.message}>
            <Input id="email" type="email" autoComplete="username" invalid={!!errors.email} {...register('email')} />
          </Field>
          <Button type="submit" block loading={req.isPending}>Enviar enlace</Button>
        </form>
      )}
      <p style={{ marginTop: 'var(--sp-4)', fontSize: 'var(--fs-sm)', textAlign: 'center' }}><Link to="/auth/login">Volver a ingresar</Link></p>
    </Card>
  );
}

export function ResetPage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const navigate = useNavigate();
  const schema = z.object({ new_password: newPassword, confirm: z.string() }).refine((v) => v.new_password === v.confirm, { path: ['confirm'], message: 'Las contraseñas no coinciden' });
  type Form = z.infer<typeof schema>;
  const { register, handleSubmit, formState: { errors } } = useForm<Form>({ resolver: zodResolver(schema) });
  const reset = useMutation({ mutationFn: (v: Form) => post<void>('/auth/password/reset', { token, new_password: v.new_password }), onSuccess: () => navigate('/auth/login', { replace: true }) });
  if (!token) return <Card title="Enlace inválido"><Alert kind="error">El enlace no contiene un token válido. Solicita uno nuevo.</Alert><Link to="/auth/reset-request">Solicitar nuevo enlace</Link></Card>;
  return (
    <Card title="Define tu nueva contraseña">
      {reset.isError && <Alert kind="error">{errorMessage(reset.error)}</Alert>}
      <form onSubmit={handleSubmit((v) => reset.mutate(v))} noValidate>
        <Field label="Nueva contraseña" htmlFor="new" hint="Mínimo 12 caracteres" error={errors.new_password?.message}>
          <Input id="new" type="password" autoComplete="new-password" invalid={!!errors.new_password} {...register('new_password')} />
        </Field>
        <Field label="Confirmar" htmlFor="confirm" error={errors.confirm?.message}>
          <Input id="confirm" type="password" autoComplete="new-password" invalid={!!errors.confirm} {...register('confirm')} />
        </Field>
        <Button type="submit" block loading={reset.isPending}>Guardar</Button>
      </form>
    </Card>
  );
}
